import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import { findElementByTagName } from "@/internals/elements";
import { isEmptySlot } from "@/internals/slot";

import styles from "./atlas-page.scss";

export type PageProps = AtlasElementProps & {
    "container": boolean;
    "fixed-header": boolean;
    "fixed-footer": boolean;
    "fluid-content": boolean;
    "uniform-padding-header": boolean;
};

/**
 * @slot header - Slot onde deve ser incluído o cabeçalho da página
 * @slot content - Slot onde deve ser incluído o conteúdo da página
 * @slot footer - Slot onde deve ser incluído o rodapé da página

 * @tag atlas-page
 */
@customElement("atlas-page")
export default class AtlasPage extends AtlasElement {
    static styles = styles;

    /** Indica se a página vai ter o comportamento de um container (Centralizando o conteúdo na página) */
    @property({ type: Boolean, reflect: true }) container: boolean = false;

    /** Indica que o cabeçalho da página será fixo */
    @property({ type: Boolean, reflect: true, attribute: "fixed-header" }) fixedHeader: boolean = false;

    /** Indica que o rodapé da página será fixo */
    @property({ type: Boolean, reflect: true, attribute: "fixed-footer" }) fixedFooter: boolean = false;

    /** Define se a altura do conteúdo da página deve ocupar 100% do espaço disponível */
    @property({ type: Boolean, reflect: true, attribute: "fluid-content" }) fluidContent: boolean;

    /** Define se o espaçamento interno do **cabeçalho fixo** deve ser uniforme (igual em todos os lados) */
    @property({ type: Boolean, reflect: true, attribute: "uniform-padding-header" }) uniformPaddingHeader = false;

    @state() private _hasHeader = false;

    @state() private _hasFooter = false;

    private hasFixedHeader() {
        return this._hasHeader && this.fixedHeader;
    }

    private hasFixedFooter() {
        return this._hasFooter && this.fixedFooter;
    }

    private async onHeaderChange() {
        await this.updateComplete;

        this._hasHeader = !isEmptySlot(this, "header");
    }

    private async onFooterChange() {
        await this.updateComplete;

        this._hasFooter = !isEmptySlot(this, "footer");
    }

    private async onContentChange() {
        await this.updateComplete;

        const contentSlot = this.shadowRoot.querySelector("slot[name='content']") as HTMLSlotElement;
        const assignedElements = contentSlot?.assignedElements() || [];
        const pageContent = findElementByTagName(assignedElements, "ATLAS-PAGE-CONTENT");

        if (!pageContent) return;

        if (this.fluidContent) {
            pageContent.toggleAttribute("fluid", true);
        } else if (pageContent.hasAttribute("fluid")) {
            this.fluidContent = true;
        }
    }

    private renderScrollableArea() {
        const scrollableAreaClass = {
            "page-scrollable-area": true,
            "has-fluid-content": this.fluidContent,
            "has-fixed-header": this.hasFixedHeader(),
            "has-fixed-footer": this.hasFixedFooter(),
            "has-container": this.container
        };

        const scrollableContentClass = {
            "page-scrollable-content": true,
            "container": this.container
        };

        return html`
            <div class=${classMap(scrollableAreaClass)}>
                <div class=${classMap(scrollableContentClass)}>
                    ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
                </div>
            </div>
        `;
    }

    private renderHeader() {
        const headerClass = {
            "page-header": true,
            "hidden": !this._hasHeader
        };

        return when(
            !this.fixedHeader,
            () => html`
                <div class=${classMap(headerClass)}>
                    <slot name="header" @slotchange=${this.onHeaderChange}></slot>
                </div>
            `
        );
    }

    private renderContent() {
        return html`
            <div class="page-content">
                <slot name="content" @slotchange=${this.onContentChange}></slot>
            </div>
        `;
    }

    private renderFooter() {
        const footerClass = {
            "page-footer": true,
            "hidden": !this._hasFooter
        };

        return when(
            !this.fixedFooter,
            () => html`
                <div class=${classMap(footerClass)}>
                    <slot name="footer" @slotchange=${this.onFooterChange}></slot>
                </div>
            `
        );
    }

    private renderFixedHeader() {
        const headerClass = {
            "page-fixed-header": true,
            "hidden": !this._hasHeader,
            "has-uniform-padding": this.uniformPaddingHeader
        };

        const headerContentClass = {
            container: this.container
        };

        return when(
            this.fixedHeader,
            () => html`
                <div class=${classMap(headerClass)}>
                    <div class=${classMap(headerContentClass)}>
                        <slot name="header" @slotchange=${this.onHeaderChange}></slot>
                    </div>
                </div>
            `
        );
    }

    private renderFixedFooter() {
        const footerClass = {
            "page-fixed-footer": true,
            "hidden": !this._hasFooter
        };

        const footerContentClass = {
            container: this.container
        };

        return when(
            this.fixedFooter,
            () => html`
                <div class=${classMap(footerClass)}>
                    <div class=${classMap(footerContentClass)}>
                        <slot name="footer" @slotchange=${this.onFooterChange}></slot>
                    </div>
                </div>
            `
        );
    }

    /** @internal */
    public render() {
        const pageClass = {
            "page": true,
            "has-fixed-header": this.hasFixedHeader(),
            "has-fixed-footer": this.hasFixedFooter()
        };

        return html`
            <div class=${classMap(pageClass)}>
                ${this.renderFixedHeader()} ${this.renderScrollableArea()} ${this.renderFixedFooter()}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-page": AtlasPage;
    }
}
